import { logError, logWarning } from '@ff-os/shared-utils/logging';
import cuid from 'cuid';
import { loadDOMImage } from 'ff-canvas';
import type { FaceDetection, FaceDetectionWorkerReply, FailedPayload, SuccessPayload } from 'ff-face-detection';
import { createFaceDetectionWebWorker, importFaceDetectionModule } from './loadFaceDetectionWebWorker';

export const detectFacesIn = async (
    thumbnailUrl: string,
    originalWidth: number,
    originalHeight: number
): Promise<FaceDetection[]> => {

    const { canRunFaceDetection, getImageDataOf, rescaleDetections } = await importFaceDetectionModule();

    if (!canRunFaceDetection()) {
        logWarning('WebGl is not supported.');
        return [];
    }

    const thumbnailElement = await loadDOMImage(thumbnailUrl, 'anonymous');
    const thumbnailData = getImageDataOf(thumbnailElement);

    const thumbnailDetections = await getDetectionsInWorker(thumbnailData);

    return rescaleDetections(
        thumbnailDetections,
        thumbnailData.width,
        thumbnailData.height,
        originalWidth,
        originalHeight
    ) as FaceDetection[];
};

export const getDetectionsInWorker = (
    imageData: ImageData
): Promise<FaceDetection[]> => {

    return new Promise(async (resolve) => {
        
        const worker = await createFaceDetectionWebWorker();

        if (!worker) {
            resolve([]);
            return;
        }

        const id = cuid();
        const payload = {
            width: imageData.width,
            height: imageData.height,
            buffer: imageData.data.buffer
        };

        worker.postMessage({
            id,
            payload
        }, [imageData.data.buffer]);

        worker.addEventListener('message', (message: MessageEvent<FaceDetectionWorkerReply<SuccessPayload | FailedPayload>>) => {
            if (message.data.id === id && message.data.success) {
                const payload = message.data.payload as SuccessPayload;
                resolve(payload.detections);
            } else if (message.data.id === id && !message.data.success) {
                const payload = message.data.payload as FailedPayload;
                logError('An error occurred while detecting faces in the image. Details: ', payload.error);
                resolve([]);
            }
        });
    });
}